import { ActionIcon, createStyles, Group, Tooltip } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { Icon, Text } from '@repo/foundations';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import SearchBox from '../../../components/SearchBox/SearchBox';
import { NavBarBreadcrumbWrapper } from '../../../components/SecodaAppShell/SecodaNavBar/NavBarBreadcrumbWrapper';
import { setParamsInUrl } from '../../../utils/url';
import { ANALYTICS_EVENTS } from '../constants';
import { inboxFeatureStore } from '../store';
import type { IInboxListHeaderProps } from '../types';
import { ActionMenu } from './actions/ActionMenu';
import { InboxFilter } from './InboxFilter';
import { InboxFilterPills } from './InboxFilterPills';

const useStyles = createStyles(
	(
		theme,
		{
			showSearch,
			filterVisible,
		}: { showSearch: boolean; filterVisible: boolean }
	) => ({
		container: {
			width: '100%',
			padding: `${theme.spacing.sm} ${theme.spacing.xs} ${theme.spacing.sm} ${theme.spacing.xl}`,
			maxHeight: !filterVisible ? 52 : 'none',
		},
		dropdown: {
			maxHeight: 500,
			overflowY: 'auto',
		},
		actionsContainer: {
			width: showSearch ? '100%' : 'auto',
		},
	})
);

const containerVariants = {
	hidden: { opacity: 0, scale: 0.9 },
	visible: { opacity: 1, scale: 1 },
	exit: { opacity: 0, scale: 0.9 },
};

const transitionProps = { duration: 0.12, ease: 'easeInOut' } as const;

export const InboxListHeader = observer(
	({
		searchTerm,
		setSearchTerm,
		events,
		setEvents,
		selected,
		inboxEventTypes,
		opened,
		setOpened,
		handleDeleteAll,
		handleDeleteAllRead,
		handleMarkAllAsRead,
		handleDelete,
		handleMarkAsRead,
		navigate,
		user,
		workspace,
		onSearchClick,
		filterOpened,
		setFilterOpened,
		filterSearchTerm,
		setFilterSearchTerm,
		notifications,
		unreadNotifications,
		markAsRead,
		restoreAllNotifications,
	}: IInboxListHeaderProps) => {
		const { classes } = useStyles({
			showSearch: inboxFeatureStore.showSearch,
			filterVisible: (events?.length ?? 0) > 0,
		});

		const handleTermChange = (term: string) => {
			setSearchTerm(term);
			setParamsInUrl('searchTerm', term);

			if (term.trim()) {
				trackEvent(
					ANALYTICS_EVENTS.SEARCH_QUERY,
					{
						searchTerm: term,
						searchLength: term.length.toString(),
						hasFilters: 'false', // Can be enhanced to check for active filters
					},
					user,
					workspace
				);
			}
		};

		const handleClose = useCallback(() => {
			trackEvent(
				ANALYTICS_EVENTS.SEARCH_CLOSE,
				{
					previousSearchTerm: searchTerm || '',
					hadSearchContent: (searchTerm?.length > 0).toString(),
				},
				user,
				workspace
			);

			setSearchTerm('');
			inboxFeatureStore.setShowSearch(false);
		}, [setSearchTerm, searchTerm, user, workspace]);

		const handleOpen = useCallback(() => {
			trackEvent(
				ANALYTICS_EVENTS.SEARCH_OPEN,
				{
					currentSearchTerm: searchTerm || '',
					hasExistingSearch: (searchTerm?.length > 0).toString(),
				},
				user,
				workspace
			);

			onSearchClick();
			inboxFeatureStore.setShowSearch(true);
		}, [onSearchClick, searchTerm, user, workspace]);

		return (
			<Group className={classes.container} position="apart">
				<AnimatePresence initial={false} exitBeforeEnter>
					{!inboxFeatureStore.showSearch && (
						<motion.div
							key="header-actions"
							animate="visible"
							exit="exit"
							variants={containerVariants}
							transition={transitionProps}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							<Group spacing="xs">
								<NavBarBreadcrumbWrapper>
									<Text sx={{ cursor: 'default' }} size="sm" weight="semibold">
										Inbox
									</Text>
								</NavBarBreadcrumbWrapper>
								<ActionMenu
									selected={selected}
									hidden={inboxFeatureStore.showSearch}
									opened={opened}
									setOpened={setOpened}
									handleDeleteAll={handleDeleteAll}
									handleDeleteAllRead={handleDeleteAllRead}
									handleMarkAllAsRead={handleMarkAllAsRead}
									handleDelete={handleDelete}
									handleMarkAsRead={handleMarkAsRead}
									navigate={navigate}
									user={user}
									workspace={workspace}
									notifications={notifications}
									markAsRead={markAsRead}
									restoreAllNotifications={restoreAllNotifications}
								/>
							</Group>
							<Group className={classes.actionsContainer} spacing="xs">
								<InboxFilter
									events={events}
									setEvents={setEvents}
									inboxEventTypes={inboxEventTypes}
									opened={filterOpened}
									setOpened={setFilterOpened}
									filterSearchTerm={filterSearchTerm}
									setFilterSearchTerm={setFilterSearchTerm}
									notifications={notifications}
									unreadNotifications={unreadNotifications}
									user={user}
									workspace={workspace}
								/>
								<Tooltip label="Search">
									<ActionIcon size="sm" p={4} onClick={handleOpen}>
										<Icon name="search" color="icon/primary/default" />
									</ActionIcon>
								</Tooltip>
							</Group>
						</motion.div>
					)}
					{inboxFeatureStore.showSearch && (
						<motion.div
							key="search-box"
							animate="visible"
							exit="exit"
							variants={containerVariants}
							transition={transitionProps}
							style={{ width: '100%' }}
						>
							<SearchBox
								alwaysShowCancel
								defaultSearchTerm={searchTerm}
								onSearch={handleTermChange}
								onCancelSearch={handleClose}
								placeholder="Search"
							/>
						</motion.div>
					)}
				</AnimatePresence>
				<InboxFilterPills />
			</Group>
		);
	}
);
